import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import TagCloud from '../TagCloud/TagCloud';
import './AssetForm.css';
import AssetValidityFormComponent from '../AssetValidityFormComponent/AssetValidityFormComponent';

export default class AssetForm extends Component {
  state = {
    name: this.props.asset?.name || '',
    description: this.props.asset?.description || '',
  };

  componentDidUpdate(prevProps) {
    const { asset } = this.props;

    if (asset && asset !== prevProps.asset) {
      this.setState({
        name: asset.name || '',
        description: asset.description || '',
      });
    }
  }

  onChange = event => {
    const { onChange } = this.props;
    const { name, value } = event.target;

    onChange(name, value);
  };

  onChangeCheckbox = event => {
    const { onChange } = this.props;
    const { name, checked } = event.target;

    onChange(name, checked);
  };

  onChangeTag = tags => {
    const { onChange } = this.props;

    onChange('tags', tags);
  };

  validate = () => {
    const {
      asset: { name },
    } = this.props;

    return Boolean(name.trim());
  };

  submit = event => {
    const { onUpdate } = this.props;

    event.preventDefault();

    if (this.validate()) {
      onUpdate();
    }
  };

  /**
   * Handles changes to the start date, ensuring it is valid and properly formatted before passing it to the parent component.
   * @param {Date} date
   */
  changeStartTs = date => {
    const { onChange } = this.props;

    onChange('validityStart', date.toISOString());
  };

  /**
   * Handles changes to the end date, ensuring it is valid and properly formatted before passing it to the parent component.
   * @param {Date} date
   */
  changeEndTs = date => {
    const { onChange } = this.props;

    onChange('validityEnd', date.toISOString());
  };

  /**
   * Handles changes to the validity enabled checkbox, passing the new state to the parent component.
   * @param {boolean} enabled
   */
  setValidity = enabled => {
    const { onChange } = this.props;

    onChange('validityEnabled', enabled);
  };

  errorClass = () => {
    const {
      asset: { name },
    } = this.props;

    return name ? null : 'error';
  };

  render() {
    const {
      auth: {
        user: {
          permissions: { Writer, Approver },
        },
      },
      asset,
      onApprove,
      onCancel,
    } = this.props;
    const state = this.state;

    return (
      <div
        id="edit"
        className="form-signs lg:col-span-8 mt-8 bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-gray-200 p-4"
      >
        <form data-test="form-group" className="space-y-6" onSubmit={this.submit}>
          {!asset.approved && <Alert bsStyle="warning">This asset requires approval</Alert>}
          <div className="grid grid-cols-2 gap-4">
            <div className="relative group">
              {this.errorClass() && (
                <span className="text-sunrise font-bold  mt-1 mb-1">
                  An error occurred, perhaps name is invalid
                </span>
              )}
              <label
                htmlFor="asset-input-name"
                className=" form-label text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Name
              </label>
              <input
                id="asset-input-name"
                data-test="input-name"
                type="text"
                name="name"
                value={state.name}
                onChange={e => this.setState({ name: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!Writer}
                className="w-full text-xl font-bold px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none"
              />
            </div>

            <div className="relative group">
              <label
                htmlFor="asset-input-description"
                className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 absolute -top-2 left-6 bg-white px-2 transition-all group-focus-within:text-warmth"
              >
                Description
              </label>
              <textarea
                id="asset-input-description"
                data-test="input-desc"
                rows="1"
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                value={state.description}
                onChange={e => this.setState({ description: e.target.value })}
                onBlur={e => this.onChange(e)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.stopPropagation();
                    this.onChange(e);
                  }
                }}
                disabled={!Writer}
                name="description"
                className="w-full text-sm font-medium px-2 py-3 rounded-2xl border border-gray-200 focus:border-warmth focus:ring-0 transition-all outline-none resize-none"
              />
            </div>

            <TagCloud tags={asset.tags} onChange={this.onChangeTag} disabled={!Writer} />
          </div>
          {asset.type === 'video' && (
            <AssetValidityFormComponent
              enabled={Boolean(asset.validityEnabled)}
              setValidity={this.setValidity}
              validityStart={new Date(asset.validityStart)}
              changeStartTs={this.changeStartTs}
              validityEnd={new Date(asset.validityEnd)}
              changeEndTs={this.changeEndTs}
            />
          )}

          <div className="pt-6 flex flex-col sm:flex-row gap-4">
            {Writer && (
              <button
                data-test="button-update"
                type="submit"
                className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
                disabled={!this.validate()}
              >
                Save Changes
              </button>
            )}
            {Approver && !asset.approved && (
              <button
                type="button"
                className="flex-1 bg-btn-primary-form text-white px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-black/20"
                data-test="button-approve"
                onClick={onApprove}
              >
                Approve
              </button>
            )}
            <button
              type="button"
              className="px-8 py-4 rounded-2xl font-bold uppercase tracking-widest text-xs text-gray-400 hover:text-black transition-colors border border-transparent hover:border-gray-200"
              data-test="button-cancel"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }
}

AssetForm.propTypes = {
  auth: PropTypes.object.isRequired,
  asset: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
